import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function PrivacyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" data-testid="privacy-page">
      <Header />
      <section className="pt-32 pb-8 bg-muted/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tighter text-foreground">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mt-2">Last updated: April 2026</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12 prose-custom">
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">1. Introduction</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Open Sky Adventure Academy ("OSAA," "we," "us," or "our") is committed to protecting the privacy and security of the personal data
                of all visitors, users, and institutional partners who interact with our website (www.osaacademy.in) and our services. This Privacy
                Policy is in compliance with the Digital Personal Data Protection Act, 2023 (DPDP Act) and its Rules, and explains how we collect,
                use, disclose, and safeguard your information.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">2. Information We Collect</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-3">We may collect the following categories of information:</p>
              <ul className="space-y-2 text-sm text-muted-foreground font-light">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Inquiry Data:</strong> Name of Principal/Director, designation, school name, city, contact number, email address, program of interest, preferred visit dates, number of students, grade levels, and any special requirements.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Website Usage Data:</strong> IP address, browser type, pages visited, time spent on pages, and other analytical data collected through cookies and similar technologies.</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span><strong className="text-foreground">Booking Data:</strong> Information provided during the booking process, including student lists, faculty identification documents (Aadhaar Cards), and intent letters.</span></li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">3. Purpose of Processing</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-3">We process your personal data for the following purposes:</p>
              <ul className="space-y-2 text-sm text-muted-foreground font-light">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>To respond to inquiries and provide information about our programs</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>To process bookings and manage program logistics</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>To comply with safety and regulatory requirements (e.g., aerodrome access clearances)</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>To improve our website and services</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>To send communications related to our programs (with consent)</span></li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">4. Legal Basis for Processing</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                We process personal data based on: (a) your consent, provided when you submit an inquiry or booking; (b) legitimate uses
                as permitted under the DPDP Act, including compliance with legal obligations and performance of contractual obligations with
                partner institutions.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">5. Data Sharing</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                We do not sell your personal data. We may share information with: (a) government authorities as required for aerodrome access
                clearances (GFTS); (b) insurance providers for program liability coverage; (c) transportation partners for logistical coordination;
                (d) service providers who assist in website operations under strict confidentiality agreements.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">6. Children's Privacy</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Our programs serve students from Nursery through Grade 12. We do not directly collect personal data from children. All data
                collection is conducted through partner schools and institutional representatives who are responsible for obtaining appropriate
                parental consent. We rely on our partner schools to obtain verifiable parental consent before sharing any student information with us.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">7. Cookies</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Our website uses cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and
                understand where our visitors are coming from. You can control cookie preferences through your browser settings.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">8. Data Security</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access,
                alteration, disclosure, or destruction. This includes encrypted data transmission, secure server infrastructure, and
                restricted access controls.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">9. Data Retention</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by
                applicable law. Inquiry data is retained for up to 24 months. Booking and program data is retained for up to 7 years for
                compliance and insurance purposes.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">10. Your Rights</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-3">
                Under the DPDP Act, you have the following rights as a Data Principal:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground font-light">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Right to access your personal data</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Right to correction and erasure of your data</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Right to withdraw consent at any time</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" /><span>Right to grievance redressal</span></li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">11. Grievance Officer</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                If you have any concerns regarding the processing of your personal data, please contact our Grievance Officer:
              </p>
              <div className="mt-3 p-4 border border-border/60 bg-card">
                <p className="text-sm text-foreground font-medium">Open Sky Adventure Academy</p>
                <p className="text-sm text-muted-foreground font-light">Email: privacy@osaacademy.in</p>
                <p className="text-sm text-muted-foreground font-light">Phone: +91 8618396055</p>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-medium text-foreground mb-3">12. Changes to This Policy</h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
                We encourage you to review this policy periodically.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
